// js/lib/push.js
// Web Push subscription manager.
//
// Call initPush(userId, shopId) once the customer is authenticated and the
// shop context is known.
// It will:
//   1. Register the service worker (if not already registered)
//   2. Check/request Notification permission
//   3. Subscribe the browser to push via PushManager
//   4. Persist the subscription to Firestore under
//      shops/{shopId}/memberships/{userId}.pushSubscriptions.<deviceKey>
//
// Each browser/device generates a unique PushSubscription. We derive a stable
// 32-char hex key from the subscription endpoint (SHA-256) and store it as a
// field inside the pushSubscriptions map. This means a single user can receive
// notifications on all their subscribed devices simultaneously.
//
// The VAPID public key is injected at build time via the VITE_VAPID_PUBLIC_KEY
// environment variable (same key used by the Cloud Function's web-push config).

import { appState } from "../state.js";
import { savePushSubscription } from "./firestore.js";

// ─── Internal: subscribe helper (called after permission is confirmed) ────────
async function _subscribe(reg, userId, shopId) {
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
  const key = await endpointToKey(subscription.endpoint);
  console.debug("[push] New subscription key:", key);
  // Tag the subscription with the shopId it was registered from so the Cloud
  // Function can skip subscriptions belonging to a different shop.
  await savePushSubscription(shopId, userId, key, {
    ...subscription.toJSON(),
    _shopId: shopId,
  });
  console.info("[push] ✅ Push subscription saved for user:", userId);
  return true;
}

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Convert a base64url VAPID public key to the Uint8Array that
 * PushManager.subscribe() expects as applicationServerKey.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * Derive a stable, Firestore-safe map key from a push subscription endpoint.
 * Uses SHA-256 via the Web Crypto API → returns the first 32 hex characters.
 * The result is deterministic: the same endpoint always produces the same key.
 *
 * @param {string} endpoint  The subscription endpoint URL
 * @returns {Promise<string>}
 */
async function endpointToKey(endpoint) {
  const encoded = new TextEncoder().encode(endpoint);
  const buf = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}

// ─── SW message listener (module-level, set up once) ─────────────────────────
// Handles subscription updates pushed by the SW when FCM rotates the endpoint.

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("message", async (event) => {
    const uid = appState.user?.uid;
    const shopId = appState.shopSlug ? appState.shop?.id : null;
    if (!uid || !shopId) return;

    if (event.data?.type === "PUSH_SUBSCRIPTION_UPDATED") {
      // SW re-subscribed autonomously — save the new subscription to Firestore.
      const sub = event.data.subscription;
      if (!sub) return;
      const key = await endpointToKey(sub.endpoint);
      console.info("[push] SW rotation: new key", key, "for user", uid);
      await savePushSubscription(shopId, uid, key, sub);
      console.info(
        "[push] Subscription updated after FCM rotation for user:",
        uid,
      );
    } else if (event.data?.type === "PUSH_RESUBSCRIBE") {
      // SW couldn't re-subscribe (no cached VAPID key) — let the client do it.
      console.info("[push] Re-subscribing push after rotation (SW request)...");
      await initPush(uid, shopId);
    }
  });
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Register the service worker for PWA installability.
 * Called at app boot, independently of push notification permission.
 * Safe to call multiple times — the browser deduplicates registrations.
 *
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
    console.debug("[sw] Service worker registered:", reg.scope);
    return reg;
  } catch (err) {
    console.warn("[sw] Service worker registration failed:", err);
    return null;
  }
}

/**
 * Initialize push notifications for the currently authenticated customer.
 * Safe to call on every page load — it bails out silently if:
 *   - the browser doesn't support push
 *   - the user has already denied permission
 *   - the same subscription is already stored
 *
 * @param {string} userId  Firebase Auth UID of the current customer
 * @param {string} shopId  Firestore shop document ID (used to store subscription per-membership)
 */
export async function initPush(userId, shopId) {
  if (!userId || !shopId) return;

  // Guard: push requires service worker + PushManager support.
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.info("[push] Web Push not supported in this browser.");
    return;
  }

  if (!VAPID_PUBLIC_KEY) {
    console.warn("[push] VITE_VAPID_PUBLIC_KEY is not set — push disabled.");
    return;
  }

  try {
    // Register (or retrieve) the service worker (reuses any existing registration).
    const reg = await registerSW();
    if (!reg) return;

    // Wait until the SW is active before subscribing.
    await navigator.serviceWorker.ready;

    // Give the SW the VAPID public key so it can re-subscribe autonomously
    // when pushsubscriptionchange fires (e.g. FCM rotates the endpoint).
    if (reg.active) {
      reg.active.postMessage({ type: "SET_VAPID_KEY", key: VAPID_PUBLIC_KEY });
    }

    console.debug("[push] initPush() started for user:", userId);
    console.debug("[push] Notification.permission:", Notification.permission);

    // Check existing permission first — don't prompt if already denied.
    if (Notification.permission === "denied") {
      console.info("[push] Notification permission denied by user.");
      return;
    }

    // Check if there's already a valid subscription for this browser.
    const existingSubscription = await reg.pushManager.getSubscription();
    console.debug(
      "[push] PushManager existing subscription:",
      existingSubscription ? existingSubscription.endpoint : "none",
    );

    if (existingSubscription) {
      const key = await endpointToKey(existingSubscription.endpoint);
      console.debug("[push] Derived key for existing subscription:", key);

      // Guard against the stale-subscription loop:
      // The Cloud Function prunes dead subscriptions (400/410 from FCM).
      // On the client side, if the browser has a valid subscription, always
      // re-save it — regardless of whether its key is already in Firestore.
      // This stops the unsubscribe→resubscribe loop that was accumulating
      // new entries on every login.
      try {
        // Save the current subscription — adds this device if new, or
        // overwrites if the endpoint rotated. Other devices' keys are untouched.
        // Tag with _shopId so the Cloud Function delivers only to subscriptions
        // registered from THIS shop's app, not from a different shop's app.
        console.debug("[push] Saving current subscription for this device.");
        await savePushSubscription(shopId, userId, key, {
          ...existingSubscription.toJSON(),
          _shopId: shopId,
        });
        console.info("[push] ✅ Subscription confirmed for user:", userId);
        return;
      } catch (err) {
        // If the Firestore write fails, log and continue — non-fatal.
        console.warn(
          "[push] Firestore write failed during subscription save:",
          err.message,
        );
        return;
      }
    }

    // No existing subscription — decide whether to auto-prompt.
    // Safari / iOS WebKit enforces that Notification.requestPermission() must
    // come from a user gesture; calling it programmatically silently results in
    // "denied". Chrome (Android, desktop) has no such restriction.
    const isSafari =
      /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
      // iOS PWA (standalone) reports as Safari but userAgent may vary
      (navigator.standalone === true &&
        /iphone|ipad|ipod/i.test(navigator.userAgent));

    if (isSafari) {
      console.info(
        "[push] Safari/iOS detected — awaiting user gesture to request permission.",
      );
      return "needs-permission";
    }

    // Non-Safari (Chrome, Android, etc.) — auto-prompt is allowed.
    console.debug("[push] Requesting permission automatically...");
    const permission = await Notification.requestPermission();
    console.debug("[push] Permission result:", permission);
    if (permission !== "granted") {
      console.info("[push] User declined notification permission.");
      return;
    }
    await _subscribe(reg, userId, shopId);
  } catch (err) {
    // Non-fatal — push is a best-effort feature.
    console.warn("[push] Could not initialize push:", err.message);
  }
}

/**
 * Request push notification permission and subscribe.
 * MUST be called from a direct user gesture (click/tap) — Safari and iOS
 * enforce this strictly and will silently deny otherwise.
 *
 * @param {string} userId
 * @param {string} shopId
 * @returns {Promise<boolean>}  true if subscribed successfully
 */
export async function requestPushPermission(userId, shopId) {
  if (!userId || !shopId) return false;
  if (!("serviceWorker" in navigator) || !("PushManager" in window))
    return false;
  if (!VAPID_PUBLIC_KEY) return false;

  try {
    const reg = await registerSW();
    if (!reg) return false;
    await navigator.serviceWorker.ready;

    const permission = await Notification.requestPermission();
    console.debug("[push] Permission result:", permission);
    if (permission !== "granted") {
      console.info("[push] User declined notification permission.");
      return false;
    }

    await _subscribe(reg, userId, shopId);
    return true;
  } catch (err) {
    console.warn("[push] requestPushPermission failed:", err.message);
    return false;
  }
}
